import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import TableCheckBox from 'components/table/TableCheckBox'
import { fail, success } from 'features/toast/Toast'
import useMemoComparator from 'utils/hooks/useMemoComparator'
import {
  CampaignTypeNames,
  CampaignTypes,
  CreativeTypes,
  DashboardTypes,
  getFinalKeywordReviewStatus,
  OperationStatus,
  ReviewStatus,
  ReviewTargetType,
} from 'features/Codes'
import OperationStatusDisplay from 'features/shared/OperationStatusDisplay'
import classNames from 'classnames'
import Switch from 'components/form/Switch'
import NullableNumber from 'features/dashboard/table/shared/NullableNumber'
import { updateOnOffStatus } from 'features/dashboard/operation/onOff/onOffSlice'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import { useDashboardTypeName } from 'features/dashboard/utils/hooks/useDashboardTypeName'
import { dateformat, isPresent, noop } from 'utils'
import Moment from 'react-moment'
import CampaignLink from 'features/dashboard/shared/CampaignLink'
import { adGroupTableActions } from 'features/dashboard/table/adGroup/adGroupTableSlice'
import { keywordTableActions } from 'features/dashboard/table/keyword/keywordTableSlice'
import { creativeTableActions } from 'features/dashboard/table/creative/creativeTableSlice'
import Wrap from 'components/Wrap'
import Tooltip from 'components/tooltip/Tooltip'
import Button from 'components/form/Button'
import AdGroupLink from 'features/dashboard/shared/AdGroupLink'
import { useDashboardTableActions } from 'features/dashboard/utils/hooks/useDashboardTableActions'
import { useDashboardTableSelector } from 'features/dashboard/utils/hooks/useDashboardTableSelector'
import { getStatusMap } from 'features/dashboard/operation/onOff/OnOffButtons'
import {
  getCampaignType,
  isSupportedMetric,
  RateTypes,
} from '../../utils/dashboardUtils'
import { showReviewReason } from '../../../reviewReason/reviewReasonSlice'

export const BaseCell = ({
  className = 'td_tbl',
  center,
  children,
  onClick = noop,
}) => (
  <div
    className={classNames(className, { align_c: center })}
    style={{ width: 'inherit' }}
    onClick={onClick}>
    <div className="inner_tbl">{children}</div>
  </div>
)
BaseCell.propTypes = {
  className: PropTypes.string,
  center: PropTypes.any,
  children: PropTypes.node,
  onClick: PropTypes.func,
}

export const SelectionCell = ({
  value,
  row: {
    original: { deleted, creativeLinkId },
  },
}) => {
  const dispatch = useDispatch()
  const { type } = useSelector(state => state.dashboard.common)
  const { selectedIds, editable, isLoading } = useDashboardTableSelector()
  const { toggleSelectedId } = useDashboardTableActions()
  const id = type === DashboardTypes.CREATIVE ? creativeLinkId : value
  const checked = selectedIds.includes(id)
  const disabled = editable || isLoading || deleted
  const handleCheckedChange = useCallback(() => {
    batch(() => {
      dispatch(toggleSelectedId(id))
      if (type === DashboardTypes.CAMPAIGN) {
        dispatch(adGroupTableActions.setPage(0))
        dispatch(keywordTableActions.setPage(0))
        dispatch(creativeTableActions.setPage(0))
      } else if (type === DashboardTypes.AD_GROUP) {
        dispatch(keywordTableActions.setPage(0))
        dispatch(creativeTableActions.setPage(0))
      }
    })
  }, [dispatch, toggleSelectedId, id, type])
  return (
    <BaseCell center>
      <TableCheckBox
        value={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
      />
    </BaseCell>
  )
}
SelectionCell.propTypes = {
  value: PropTypes.string,
  row: PropTypes.shape({
    original: PropTypes.shape({
      deleted: PropTypes.bool,
      creativeLinkId: PropTypes.string,
    }),
  }),
}
export const OnOffCell = ({
  value,
  row: {
    original: { id, creativeLinkId, deleted },
  },
}) => {
  const dispatch = useDispatch()
  const { isSubmitting } = useSelector(state => state.dashboard.operation.onOff)
  const { type, name } = useDashboardTypeName()
  const { setStatus } = useDashboardTableActions()
  const handleOnOffChange = async () => {
    const { payload, error } = await dispatch(
      updateOnOffStatus({
        type,
        status: value === 'ON' ? 'OFF' : 'ON',
        ids: [type === DashboardTypes.CREATIVE ? creativeLinkId : id],
      })
    )
    if (error) {
      fail(`${name} 변경사항을 적용할 수 없습니다.`)
    } else {
      const statusMap = getStatusMap(payload, type)
      dispatch(setStatus(statusMap))
      success(`${name} 변경사항이 적용되었습니다.`)
      dispatch(refreshDashboard())
    }
  }
  return (
    <BaseCell center>
      <Switch
        status={value}
        isLoading={isSubmitting}
        disabled={deleted}
        onChange={handleOnOffChange}
      />
    </BaseCell>
  )
}
OnOffCell.propTypes = {
  value: PropTypes.string,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      creativeLinkId: PropTypes.string,
      deleted: PropTypes.bool,
    }),
  }),
}
export const OperationStatusCell = ({ value: operationStatus }) => {
  const memorizedOperationStatus = useMemoComparator(
    () => operationStatus.map(_name => OperationStatus[_name]),
    shallowEqual
  )
  return (
    <BaseCell>
      <OperationStatusDisplay
        operationStatus={memorizedOperationStatus}
        container="dashboardWrap"
      />
    </BaseCell>
  )
}
OperationStatusCell.propTypes = {
  value: PropTypes.array,
}
export const MetricCell = ({ value, column: { id }, row: { original } }) => {
  const { type } = useDashboardTypeName()
  const isSupported = useMemo(() => {
    const campaignType = getCampaignType(original, type)
    if (
      campaignType === CampaignTypes.TALK_CHANNEL &&
      type === DashboardTypes.KEYWORD
    ) {
      return false
    }
    return isSupportedMetric(campaignType, id)
  }, [original, type, id])
  const isRate = useMemo(() => RateTypes.includes(id), [id])
  return (
    <BaseCell>
      <span className="align_r">
        <NullableNumber
          value={value}
          thousandSeparator
          alternative={isSupported ? (isRate ? '0%' : 0) : '-'}
          suffix={isRate ? '%' : undefined}
        />
      </span>
    </BaseCell>
  )
}
MetricCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  column: PropTypes.shape({
    id: PropTypes.string,
  }),
  row: PropTypes.shape({
    original: PropTypes.object,
  }),
}

export const IdCell = ({ row: { original }, column: { id: columnId } }) => {
  const key =
    columnId === 'creativeLinkId' || columnId === 'creativeId' ? columnId : 'id'
  return <BaseCell>{original[key]}</BaseCell>
}
IdCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }),
  column: PropTypes.shape({
    id: PropTypes.string,
  }),
}

export const DateCell = ({ value }) => (
  <BaseCell>{value && <Moment format={dateformat}>{value}</Moment>}</BaseCell>
)
DateCell.propTypes = {
  value: PropTypes.string,
}

export const CampaignCell = ({
  row: {
    original,
    original: {
      deleted,
      parentDomainInfo: {
        campaign: { id, name },
      },
    },
  },
}) => {
  const { type } = useDashboardTypeName()
  const campaignType = getCampaignType(original, type)
  const { editable } = useDashboardTableSelector()
  const disabled = useMemo(() => editable || deleted, [editable, deleted])
  return (
    <BaseCell>
      {disabled ? (
        name
      ) : (
        <CampaignLink id={id} campaignType={campaignType} className="fc_emph">
          {name}
        </CampaignLink>
      )}
    </BaseCell>
  )
}
CampaignCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      deleted: PropTypes.bool,
      parentDomainInfo: PropTypes.shape({
        campaign: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      }),
    }),
  }),
}

export const AdGroupCell = ({
  row: {
    original,
    original: {
      deleted,
      parentDomainInfo: {
        adGroup: { id, name },
        campaign: { id: campaignId },
      },
    },
  },
}) => {
  const { type } = useDashboardTypeName()
  const campaignType = getCampaignType(original, type)
  const { editable } = useDashboardTableSelector()
  const disabled = useMemo(() => editable || deleted, [editable, deleted])
  return (
    <BaseCell>
      {disabled ? (
        name
      ) : (
        <AdGroupLink
          id={id}
          campaignId={campaignId}
          campaignType={campaignType}
          className="fc_emph"
          tab={type}>
          {name}
        </AdGroupLink>
      )}
    </BaseCell>
  )
}
AdGroupCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      deleted: PropTypes.bool,
      parentDomainInfo: PropTypes.shape({
        adGroup: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        campaign: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
  }),
}

const ReviewStatusTooltips = {
  [ReviewStatus.APPROVING.name]: {
    [DashboardTypes.KEYWORD]: '키워드 심사 중 입니다.',
    [DashboardTypes.CREATIVE]: '소재 심사 중 입니다.',
  },
  [ReviewStatus.LANDING_URL_APPROVING.name]: {
    [DashboardTypes.KEYWORD]: '키워드 랜딩URL 심사 중 입니다.',
  },
  [ReviewStatus.REJECTED_APPROVAL.name]: {
    [DashboardTypes.KEYWORD]:
      '키워드 심사가이드에 맞지 않아 보류되었습니다. 보류 사유 확인 후 신규 등록하세요.',
    [DashboardTypes.CREATIVE]:
      '소재가 심사가이드에 맞지 않아 보류되었습니다. 보류 사유 확인 후 신규 등록하세요.',
  },
}
export const ReviewStatusCell = ({
  row: {
    original: {
      id,
      deleted,
      creativeId,
      reviewStatus,
      landingUrlReviewStatus,
      landingUrl,
    },
  },
}) => {
  const dispatch = useDispatch()
  const { type } = useDashboardTypeName()
  const { isLoading } = useSelector(state => state.reviewReason)
  const status =
    type === DashboardTypes.CREATIVE
      ? ReviewStatus[reviewStatus]
      : getFinalKeywordReviewStatus({
          keywordReviewStatus: ReviewStatus[reviewStatus],
          landingUrlReviewStatus: ReviewStatus[landingUrlReviewStatus],
          hasLandingUrl: isPresent(landingUrl),
        })
  const handleClick = useCallback(() => {
    dispatch(
      showReviewReason({
        id: type === DashboardTypes.CREATIVE ? creativeId : id,
        reviewTargetType:
          type === DashboardTypes.CREATIVE
            ? ReviewTargetType.CREATIVE
            : ReviewTargetType.KEYWORD,
        showSubtitle: true,
      })
    )
  }, [dispatch, creativeId, id, type])
  return (
    <BaseCell>
      <Wrap
        on={
          ReviewStatus.REJECTED_APPROVAL === status ||
          ReviewStatus.APPROVING === status ||
          ReviewStatus.LANDING_URL_APPROVING === status
        }
        wrapper={({ children }) => (
          <Tooltip
            position="bottom alignLeft"
            container="dashboardWrap"
            offsetX={10}
            offsetY={10}
            content={ReviewStatusTooltips[status.name][type]}>
            {children}
          </Tooltip>
        )}>
        {status.title}
      </Wrap>
      {ReviewStatus.REJECTED_APPROVAL === status && (
        <>
          <Button
            size="small"
            disabled={deleted}
            onClick={handleClick}
            loading={isLoading}>
            사유
          </Button>
        </>
      )}
    </BaseCell>
  )
}
ReviewStatusCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      deleted: PropTypes.bool,
      creativeId: PropTypes.string,
      reviewStatus: PropTypes.string,
      landingUrlReviewStatus: PropTypes.string,
      landingUrl: PropTypes.object,
    }),
  }),
}

export const CampaignTypeCell = ({ row: { original } }) => {
  const { type } = useDashboardTypeName()
  const campaignTypeName = useMemo(() => {
    const campaignType = getCampaignType(original, type)
    return campaignType === CampaignTypes.TALK_CHANNEL
      ? CampaignTypeNames[CampaignTypes.TALK_CHANNEL]
      : CampaignTypeNames[CampaignTypes.PREMIUM_LINK]
  }, [type, original])
  return <BaseCell>{campaignTypeName}</BaseCell>
}
CampaignTypeCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      type: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      adGroupType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      keywordType: PropTypes.oneOf([
        CampaignTypes.PREMIUM_LINK,
        CampaignTypes.TALK_CHANNEL,
      ]),
      creativeType: PropTypes.oneOf([
        CreativeTypes.BASIC,
        CampaignTypes.TALK_CHANNEL,
      ]),
    }),
  }),
}
